/**
 * Snapshot agent-selected OT rooms for public smart-offer payload.
 * Kept dependency-light (no PassengersData / SearchContext) for unit tests + quote builders.
 */

function _num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Human-readable occupancy from roomSet token (A2C1, P4, ...). */
export function parseOrganizedTourRoomSetOccupancyLabel(roomSet) {
  if (!roomSet) return null;
  const match = String(roomSet).match(/A(\d+)(?:C(\d+))?|P(\d+)/i);
  if (!match) return String(roomSet).trim() || null;

  let adults = 0;
  let kids = 0;
  if (match[3]) {
    const total = parseInt(match[3], 10);
    adults = 2;
    kids = Math.max(0, total - 2);
  } else if (match[1]) {
    adults = parseInt(match[1], 10);
    kids = match[2] ? parseInt(match[2], 10) : 0;
  }

  if (adults === 2 && kids === 0) return "\u05D6\u05D5\u05D2";
  if (adults === 2 && kids > 0) return "\u05D6\u05D5\u05D2 + " + kids;
  if (adults === 1 && kids === 0) return "\u05DE\u05D1\u05D5\u05D2\u05E8 \u05D0\u05D7\u05D3";
  if (adults === 1 && kids > 0) return "\u05DE\u05D1\u05D5\u05D2\u05E8 \u05D0\u05D7\u05D3 + " + kids + " \u05D9\u05DC\u05D3\u05D9\u05DD";
  if (kids === 0) return adults + " \u05DE\u05D1\u05D5\u05D2\u05E8\u05D9\u05DD";
  return adults + " \u05DE\u05D1\u05D5\u05D2\u05E8\u05D9\u05DD + " + kids + " \u05D9\u05DC\u05D3\u05D9\u05DD";
}

export function formatOrganizedTourSlotOccupancyFromPax(slot) {
  if (!slot || typeof slot !== "object") return "\u2014";
  const adults = Array.isArray(slot.adults) ? slot.adults.length : 0;
  const kids = Array.isArray(slot.kids) ? slot.kids.length : 0;
  const infants = Array.isArray(slot.infants) ? slot.infants.length : 0;
  const parts = [];
  if (adults > 0) parts.push(adults + " " + (adults === 1 ? "\u05DE\u05D1\u05D5\u05D2\u05E8" : "\u05DE\u05D1\u05D5\u05D2\u05E8\u05D9\u05DD"));
  if (kids > 0) parts.push(kids + " " + (kids === 1 ? "\u05D9\u05DC\u05D3" : "\u05D9\u05DC\u05D3\u05D9\u05DD"));
  if (infants > 0) parts.push(infants + " " + (infants === 1 ? "\u05EA\u05D9\u05E0\u05D5\u05E7" : "\u05EA\u05D9\u05E0\u05D5\u05E7\u05D5\u05EA"));
  return parts.length ? parts.join(", ") : "\u2014";
}

/**
 * Hebrew composition label for OT roomSet / occupancy ? aligned with PackagePriceSelector.
 * @param {{ roomSet?: string|null, adults?: number, kids?: number }} args
 */
export function formatOrganizedTourRoomCompositionLabelHe({ roomSet, adults = 0, kids = 0 }) {
  const a = Number(adults) || 0;
  const k = Number(kids) || 0;

  if (a === 1 && k === 0) return "\u05D7\u05D3\u05E8 \u05D9\u05D7\u05D9\u05D3";
  if (a === 2 && k === 0) return "\u05D7\u05D3\u05E8 \u05D6\u05D5\u05D2\u05D9";
  if (a === 3 && k === 0) return "\u05D7\u05D3\u05E8 \u05DE\u05E9\u05D5\u05DC\u05E9";
  if (a >= 4 && k === 0) return "\u05D7\u05D3\u05E8 \u05DC\u05BE" + a;
  if (a === 2 && k > 0) return "\u05D7\u05D3\u05E8 \u05D6\u05D5\u05D2\u05D9 + " + k + " \u05D9\u05DC\u05D3\u05D9\u05DD";
  if (a === 1 && k > 0) return "\u05D7\u05D3\u05E8 \u05D9\u05D7\u05D9\u05D3 + " + k + " \u05D9\u05DC\u05D3\u05D9\u05DD";

  const fromSet = parseOrganizedTourRoomSetOccupancyLabel(roomSet);
  if (fromSet) return fromSet;
  if (roomSet) return String(roomSet).trim();
  return null;
}

/**
 * Per-room display price ? same SSOT fields as sticky booking / order summary (PWCC, else commission).
 * @param {object|null|undefined} slot
 */
export function resolveOrganizedTourRoomOfferPriceIls(slot) {
  const room = slot?.room;
  if (!room || typeof room !== "object") return null;

  const pwcc = _num(room?.ourFareDetails?.ourTotalFare?.priceWithCommissionAndCreditCardFee);
  if (pwcc != null && pwcc > 0) {
    return { priceIls: Math.round(pwcc), source: "priceWithCommissionAndCreditCardFee" };
  }

  const pwc = _num(room?.ourFareDetails?.ourTotalFare?.priceWithCommission);
  if (pwc != null && pwc > 0) {
    return { priceIls: Math.round(pwc), source: "priceWithCommission" };
  }

  const manual = _num(room?.selectedAddRoomPriceIls ?? slot?.selectedAddRoomPriceIls);
  if (manual != null && manual > 0) {
    return { priceIls: Math.round(manual), source: "selectedAddRoomPriceIls" };
  }

  return null;
}

function adultsKidsFromRoomSet(roomSet) {
  if (!roomSet) return { adults: 0, kids: 0 };
  const match = String(roomSet).match(/A(\d+)(?:C(\d+))?|P(\d+)/i);
  if (!match) return { adults: 0, kids: 0 };
  if (match[3]) {
    const total = parseInt(match[3], 10);
    return { adults: 2, kids: Math.max(0, total - 2) };
  }
  return {
    adults: parseInt(match[1], 10) || 0,
    kids: match[2] ? parseInt(match[2], 10) : 0,
  };
}

/**
 * Build public-offer room rows from agent selection (roomsAndPaxInfo / selectedRoom).
 * Does not invent prices ? only persists values already on the room fare objects.
 */
export function snapshotOrganizedTourRoomsForPublicOffer(args = {}) {
  const { roomsAndPaxInfo, selectedResult = null } = args;

  let slots = Array.isArray(roomsAndPaxInfo) ? roomsAndPaxInfo.filter(Boolean) : [];

  if (slots.length === 0 && selectedResult?.selectedRoom) {
    const sr = selectedResult.selectedRoom;
    slots = [
      {
        // Prefer roomSet occupancy when selectedRoom has no pax arrays (common on deal load).
        adults: Array.isArray(sr.adults) ? sr.adults : [],
        kids: Array.isArray(sr.kids) ? sr.kids : [],
        infants: Array.isArray(sr.infants) ? sr.infants : [],
        room: sr,
      },
    ];
  }

  const withRoom = slots.filter((s) => s?.room && typeof s.room === "object");
  if (withRoom.length === 0) return null;

  const total = withRoom.length;
  return withRoom.map((slot, slotIndex) => {
    const room = slot.room;
    const roomSet = room?.roomSet ?? room?.room_set ?? null;
    const adultsFromPax = Array.isArray(slot.adults) ? slot.adults.length : 0;
    const kidsFromPax = Array.isArray(slot.kids) ? slot.kids.length : 0;
    const fromSet = adultsKidsFromRoomSet(roomSet);
    const adults = adultsFromPax > 0 ? adultsFromPax : fromSet.adults;
    const kids = kidsFromPax > 0 ? kidsFromPax : fromSet.kids;

    const composition =
      formatOrganizedTourRoomCompositionLabelHe({ roomSet, adults, kids }) ||
      (roomSet ? String(roomSet) : "\u05D7\u05D3\u05E8");

    const roomType =
      room?.roomType ?? room?.RoomType ?? room?.roomName ?? room?.room_name ?? null;

    const priced = resolveOrganizedTourRoomOfferPriceIls(slot);
    const occupancyFromPax = formatOrganizedTourSlotOccupancyFromPax(slot);

    return {
      slot_index: slotIndex,
      slot_label_he: total > 1 ? "\u05D7\u05D3\u05E8 " + (slotIndex + 1) + " \u05DE\u05EA\u05D5\u05DA " + total : "\u05D7\u05D3\u05E8",
      room_set: roomSet != null ? String(roomSet) : null,
      composition_label_he: composition,
      occupancy_label_he: occupancyFromPax !== "\u2014" ? occupancyFromPax : null,
      room_type: roomType != null && String(roomType).trim() ? String(roomType).trim() : null,
      price_ils: priced?.priceIls ?? null,
      price_source: priced?.source ?? null,
      is_manual_add_room:
        slot?.addedFromRoomOptions === true || room?.addedFromRoomOptions === true,
    };
  });
}
